
import { TableCell, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Copy } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface ErrorLog {
  id: string;
  template_id: string;
  processing_stage: string;
  error_message: string;
  created_at: string;
  retry_count: number;
  templates?: {
    filename: string;
  };
}

interface ErrorLogRowProps {
  error: ErrorLog;
  onRetry: (templateId: string) => void;
}

export function ErrorLogRow({ error, onRetry }: ErrorLogRowProps) {
  const { toast } = useToast();

  const copyErrorMessage = async () => {
    try {
      await navigator.clipboard.writeText(error.error_message);
      toast({
        title: "Copied",
        description: "Error message copied to clipboard",
      });
    } catch (err) {
      toast({
        title: "Failed to copy",
        description: "Could not copy error message to clipboard",
        variant: "destructive",
      });
    }
  };

  return (
    <TableRow>
      <TableCell>{error.templates?.filename || 'Unknown'}</TableCell>
      <TableCell>
        <Badge variant="outline">{error.processing_stage}</Badge>
      </TableCell>
      <TableCell className="max-w-md">
        <div className="flex items-center gap-2">
          <span className="truncate">{error.error_message}</span>
          <Button
            size="sm"
            variant="ghost"
            onClick={copyErrorMessage}
            className="h-6 w-6 p-0 shrink-0"
          >
            <Copy className="h-3 w-3" />
          </Button>
        </div>
      </TableCell>
      <TableCell>
        {new Date(error.created_at).toLocaleDateString()}
      </TableCell>
      <TableCell>{error.retry_count}</TableCell>
      <TableCell>
        <Button
          size="sm"
          variant="outline"
          onClick={() => onRetry(error.template_id)}
        >
          Retry
        </Button>
      </TableCell>
    </TableRow>
  );
}
